import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import TableRow from "@mui/material/TableRow";
import { ReactNode } from "react";
import Interval from "../../../models/communications/Interval";
import IntervalSet from "../../../models/communications/IntervalSet";
import Interaction from "../../../models/interactions/Interaction";
import InteractionNode from "../../../models/interactions/InteractionNode";
import InteractionNodeInterval from "../../../models/interactions/InteractionNodeInterval";
import MatrixCell from "./MatrixCell";
import MatrixControls from "./controls/MatrixControls";
import MatrixIntervalControls from "./controls/MatrixIntervalControls";
import InteractionParentInterval from "../../../models/interactions/InteractionParentInterval";
import Button from "@mui/material/Button";
import { useStoreDispatch } from "../../../store/Hooks";
import { interactionTreeSliceActions } from "../../../store/slices/InteractionTreeSlice";
import {
  compareIntervalsByDay,
  interactionsNodesHandleFetch,
  stateDeepCopy,
} from "../../../helpers/DataUpdateHelper";
import { navSliceActions } from "../../../store/slices/NavSlice";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CommunicationSet from "../../../models/communications/CommunicationSet";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Grid from "@mui/material/Grid";
import { Tooltip } from "@mui/material";
import {
  findCommSetEarliestStartDay,
  getColspanFromAssociatedIntervalsByCommSetID,
} from "../../../helpers/IntervalSetHelper";
import InteractionDeleteButton from "../../interactions/ui/InteractionDeleteButton";
import MatrixCommSetControls from "./controls/MatrixCommSetControls";

export const MatrixHeadingControlsRowStyled = styled(TableRow)(({ theme }) => ({
  div: {
    backgroundColor: "white",
    padding: "0px",
  },
}));

export interface MatrixHeadingControlsRowProps {
  children?: ReactNode;
  intervalSetID: number;
  associatedIntervals: Array<Interval>;
  associatedInteractionNodeIntervals: Array<InteractionNodeInterval>;
  associatedInteractionNodes: Array<InteractionNode>;
  associatedInteractions: Array<Interaction>;
  associatedCommSets: Array<CommunicationSet>;
  currentParent?: number | null; // needed for creating new interactions at this level
  currentSet?: number | null; // needed for creating new interactions at this level
  interactionParentIntervals?: Array<InteractionParentInterval>; // needed for creating new interactions at this level
  editMode: boolean;
  intervalSetName: string | null;
  intervalSet?: IntervalSet | null;
  addNewIntervalToIntervalSet: any;
  changesDetected:boolean;
  intervalSetSaveHandler:any
}

const MatrixCommSetsControlsRow = (props: MatrixHeadingControlsRowProps) => {
  const intervalsToCommSetsMap = new Map<
    number | null,
    { commSetName: string; colSpan: number; commSetID: number | null, intervalID: number | null }
  >();

  const iconCellStyle = {
    paddingTop: "10px",
    paddingBottom: "0px",
    textAlign: "center",
    whiteSpace: "nowrap",
    backgroundColor: "#ebebeb",
    borderRight:"2pt solid #C8C6C6",
    position: "sticky",
    left: 0,
    zIndex: 2
  };
  const controlsBorderStyle = {
    paddingTop: "10px",
    paddingBottom: "0px",
    textAlign: "center",
    whiteSpace: "nowrap",
    backgroundColor: "#f2f4f7",
    borderRight:"2pt solid #C8C6C6"

  };
  const controlsBorderStyleNoSet = {
    paddingLeft: "10px",
    paddingTop: "10px",
    paddingBottom: "0px",
    whiteSpace: "nowrap",
    backgroundColor: "#f2f4f7",
    borderRight:"2pt solid #C8C6C6"

  };

  const dispatch = useStoreDispatch();

  const handleOpenEditCommunications = (commSetID: number | null, intervalID: number | null) => {
    dispatch(
      navSliceActions.openRightDrawer({
        content: commSetID,
        type: "communications",
        resourceName: "interval",
        resourceID: intervalID,
        extraData: { editMode: props.editMode }
      }),
    );
  };

  /* start reordering logic */
  // build intervalsToCommSets Map
  for (let i = 0; i < props.associatedIntervals.length; i++) {
    const thisHereInterval = props.associatedIntervals[i];
    let key: null | number = null;
    if (thisHereInterval.communication_set_id) {
      key = thisHereInterval.communication_set_id;
    }
    const thisCommSetIndex = props.associatedCommSets.findIndex((commset) => {
      return commset.id === key && commset.name != "" && commset.name != null;
    });
    const thisCommSet = props.associatedCommSets[thisCommSetIndex];
    const commSetName = thisCommSet ? thisCommSet.name : "";
    intervalsToCommSetsMap.set(key, {
      commSetName: commSetName ? commSetName : "",
      colSpan: getColspanFromAssociatedIntervalsByCommSetID(
        thisHereInterval.communication_set_id,
        props.associatedIntervals,
      ),
      commSetID: thisCommSet ? thisCommSet.id : null,
      intervalID: thisHereInterval.id
    });
  }

  const commSetsMapEntries = [...intervalsToCommSetsMap.entries()];
  commSetsMapEntries.sort((a, b) => {
    const startDayA = findCommSetEarliestStartDay(
      a[0],
      props.associatedIntervals || [],
    );
    const startDayB = findCommSetEarliestStartDay(
      b[0],
      props.associatedIntervals || [],
    );

    return startDayA - startDayB;
  });

  const sortedIntervalsToCommSetsMap = new Map(commSetsMapEntries); // ***this successfully re-orders the commset top row - replace intervalsToCommSetsMap with sortedIntervalsToCommSetsMap
  // console.log("sorted commsets for intervalSet " + props.intervalSetID)
  // console.log(sortedIntervalsToCommSetsMap)
  /* end reordering logic */

  return (
    <MatrixHeadingControlsRowStyled>
      {/* placeholder cell */}
      <MatrixCell style={iconCellStyle}>
        <Tooltip title="Communication Sets">
          <AccessTimeIcon style={{ color: "lightgray", fontSize: "20pt" }} />
        </Tooltip>
      </MatrixCell>
      {Array.from(sortedIntervalsToCommSetsMap.entries()).map(
        ([componentName, intprops]) => (
          // eslint-disable-next-line react/prop-types
          <MatrixCommSetControls
            controlsBorderStyle={controlsBorderStyle}
            intervalID={intprops.intervalID || 0}
            componentName={intprops.commSetName}
            controlsBorderStyleNoSet={controlsBorderStyleNoSet}
            key={componentName}
            colSpan={intprops.colSpan}
            commSetName={intprops.commSetName}
            handleOpenEditCommunications={handleOpenEditCommunications}
            {...props}
            intervalSetID={props.intervalSetID}
            parentHover={false}
            intervalSetName={props.intervalSetName}
            addNewIntervalToIntervalSet={props.addNewIntervalToIntervalSet}
            commSetID={intprops.commSetID}
          />
        ),
      )}
    </MatrixHeadingControlsRowStyled>
  );
};

export default MatrixCommSetsControlsRow;
